// Schedule.tsx — экран «Расписание и напоминания» (Ф7 + произвольный cron, write).
// Редактор survey_assignments поверх /api/assignments.
//
// Расписание задаётся спецификацией, сервер собирает cron:
//  - Еженедельно: дни недели (Пн–Вс) + время;
//  - Ежемесячно: число месяца + время.
// Плюс переключатель «учитывать рабочие дни РФ» (workdays_only) и цепочка
// напоминаний (offset'ы; текст хардкожен в боте). cron, не сводимый к
// weekly/monthly, приходит как repeat='custom' — показываем raw + предупреждение.
//
// Ночное окно 22:00–09:00 и набор праздников — глобальны (read-only карточка).

import { useMemo, useState } from 'react';
import { api, ApiError } from '../api/client';
import type {
  Assignment,
  ReminderOffset,
  RespondentsResponse,
  SurveyListResponse,
} from '../api/contract';
import { StateCard } from '../components/StateBlock';
import { useApi } from '../lib/useApi';

const OFFSET_LABELS: Record<ReminderOffset, string> = {
  '5 minutes': '5 мин',
  '30 minutes': '30 мин',
  '1 hour': '1 ч',
  '2 hours': '2 ч',
  '3 hours': '3 ч',
  '4 hours': '4 ч',
  '8 hours': '8 ч',
};

const ROLE_LABELS: Record<string, string> = { director: 'директор', manager: 'менеджер' };

// ISO weekday 1=Пн … 7=Вс.
const WEEKDAYS: { iso: number; label: string }[] = [
  { iso: 1, label: 'Пн' },
  { iso: 2, label: 'Вт' },
  { iso: 3, label: 'Ср' },
  { iso: 4, label: 'Чт' },
  { iso: 5, label: 'Пт' },
  { iso: 6, label: 'Сб' },
  { iso: 7, label: 'Вс' },
];

type Repeat = 'weekly' | 'monthly';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function clampInt(v: string, min: number, max: number, fallback: number): number {
  const n = Number.parseInt(v, 10);
  if (Number.isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

export default function Schedule() {
  const state = useApi(
    () => Promise.all([api.assignments(), api.surveys(), api.respondents()]),
    [],
  );
  const [list, setList] = useState<Assignment[] | null>(null);

  if (state.loading) return <StateCard kind="loading" message="Загрузка расписания…" />;
  if (state.error || !state.data) {
    return <StateCard kind="error" message={state.error ?? 'Не удалось загрузить'} />;
  }

  const [assignmentsResp, surveys, respondents] = state.data;
  const assignments = list ?? assignmentsResp.assignments;
  const { timezones, reminderOffsets } = assignmentsResp;

  const replace = (updated: Assignment) =>
    setList((cur) =>
      (cur ?? assignmentsResp.assignments).map((a) => (a.id === updated.id ? updated : a)),
    );
  const append = (created: Assignment) =>
    setList((cur) => [...(cur ?? assignmentsResp.assignments), created]);

  return (
    <section className="page">
      <div className="ph">
        <h1>Расписание и напоминания</h1>
      </div>
      <p className="sub">
        Когда стартует опрос, как часто напоминать и когда сдаться. На каждого сотрудника и анкету —
        своё расписание: по дням недели или по числу месяца, в любое время.
      </p>

      {assignments.length === 0 ? (
        <StateCard
          kind="empty"
          message="Нет назначений. Заведите сотрудника в боте (/add-respondent) или создайте назначение ниже."
        />
      ) : (
        assignments.map((a) => (
          <AssignmentEditor
            key={a.id}
            a={a}
            timezones={timezones}
            offsets={reminderOffsets}
            onSaved={replace}
          />
        ))
      )}

      <NightModeCard />

      <CreateAssignment
        surveys={surveys}
        respondents={respondents}
        timezones={timezones}
        existing={assignments}
        onCreated={append}
      />
    </section>
  );
}

// ============================================================
// Редактор одного назначения
// ============================================================

function AssignmentEditor({
  a,
  timezones,
  offsets,
  onSaved,
}: {
  a: Assignment;
  timezones: string[];
  offsets: ReminderOffset[];
  onSaved: (updated: Assignment) => void;
}) {
  const isCustom = a.repeat === 'custom';
  const [repeat, setRepeat] = useState<Repeat>(a.repeat === 'monthly' ? 'monthly' : 'weekly');
  const [hour, setHour] = useState<number>(a.scheduleHour ?? 9);
  const [minute, setMinute] = useState<number>(a.scheduleMinute ?? 0);
  const [weekdays, setWeekdays] = useState<number[]>(
    a.weekdays.length ? a.weekdays : [1, 2, 3, 4, 5],
  );
  const [dayOfMonth, setDayOfMonth] = useState<number>(a.dayOfMonth ?? 1);
  const [workdaysOnly, setWorkdaysOnly] = useState<boolean>(a.workdaysOnly);
  const [timezone, setTimezone] = useState<string>(a.timezone);
  const [steps, setSteps] = useState<ReminderOffset[]>(a.reminderPolicy.steps.map((s) => s.offset));
  const [active, setActive] = useState<boolean>(a.isActive);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tzOptions = useMemo(
    () => (timezones.includes(timezone) ? timezones : [timezone, ...timezones]),
    [timezones, timezone],
  );

  const weeklyInvalid = repeat === 'weekly' && weekdays.length === 0;

  // dirty — что-то поменяли против сервера; для custom всегда даём сохранить
  // (нормализует произвольный cron к weekly/monthly).
  const dirty =
    isCustom ||
    repeat !== (a.repeat === 'monthly' ? 'monthly' : 'weekly') ||
    hour !== (a.scheduleHour ?? 9) ||
    minute !== (a.scheduleMinute ?? 0) ||
    weekdays.join(',') !== (a.weekdays.length ? a.weekdays : [1, 2, 3, 4, 5]).join(',') ||
    dayOfMonth !== (a.dayOfMonth ?? 1) ||
    workdaysOnly !== a.workdaysOnly ||
    timezone !== a.timezone ||
    active !== a.isActive ||
    steps.join(',') !== a.reminderPolicy.steps.map((s) => s.offset).join(',');

  const toggleWeekday = (iso: number) =>
    setWeekdays((cur) =>
      cur.includes(iso) ? cur.filter((d) => d !== iso) : [...cur, iso].sort((x, y) => x - y),
    );

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const updated = await api.saveAssignment(a.id, {
        repeat,
        scheduleHour: hour,
        scheduleMinute: minute,
        weekdays: repeat === 'weekly' ? weekdays : undefined,
        dayOfMonth: repeat === 'monthly' ? dayOfMonth : undefined,
        workdaysOnly,
        reminderPolicy: { steps: steps.map((offset) => ({ offset })) },
        timezone,
        isActive: active,
      });
      onSaved(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 16, opacity: active ? 1 : 0.6 }}>
      <div className="sched-head">
        <div className="who">
          {a.respondentName}
          <small>
            {ROLE_LABELS[a.respondentRole] ?? a.respondentRole} · {a.surveyTitle}
          </small>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {dirty && !isCustom && <span className="sched-dirty">● не сохранено</span>}
          <button className="btn" disabled={!dirty || saving || weeklyInvalid} onClick={save}>
            {saving ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </div>
      </div>

      {isCustom && (
        <p style={{ color: 'var(--yellow)', fontSize: 12, margin: '4px 0 0' }}>
          Текущее расписание — произвольный cron <span className="toks">{a.scheduleCron}</span>.
          Сохранение заменит его выбранным ниже.
        </p>
      )}
      {error && <p style={{ color: 'var(--red)', fontSize: 13, margin: '6px 0' }}>{error}</p>}

      <div className="grid2" style={{ marginTop: 14 }}>
        {/* расписание */}
        <div>
          <div className="field">
            <label>Повтор</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className={`btn${repeat === 'weekly' ? '' : ' ghost'}`}
                onClick={() => setRepeat('weekly')}
              >
                Еженедельно
              </button>
              <button
                className={`btn${repeat === 'monthly' ? '' : ' ghost'}`}
                onClick={() => setRepeat('monthly')}
              >
                Ежемесячно
              </button>
            </div>
          </div>

          <div className="field">
            <label>Время старта (локальное время сотрудника)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                className="tinput"
                style={{ width: 64 }}
                type="number"
                min={0}
                max={23}
                value={hour}
                onChange={(e) => setHour(clampInt(e.target.value, 0, 23, hour))}
              />
              <span style={{ fontSize: 18, fontWeight: 700 }}>:</span>
              <input
                className="tinput"
                style={{ width: 64 }}
                type="number"
                min={0}
                max={59}
                value={pad2(minute)}
                onChange={(e) => setMinute(clampInt(e.target.value, 0, 59, minute))}
              />
            </div>
          </div>

          {repeat === 'weekly' ? (
            <div className="field">
              <label>Дни недели</label>
              <div className="days">
                {WEEKDAYS.map((w) => (
                  <button
                    key={w.iso}
                    className={`day${weekdays.includes(w.iso) ? ' on' : ''}`}
                    onClick={() => toggleWeekday(w.iso)}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
              {weeklyInvalid && (
                <p style={{ color: 'var(--red)', fontSize: 12, margin: '6px 0 0' }}>
                  Выберите хотя бы один день.
                </p>
              )}
            </div>
          ) : (
            <div className="field">
              <label>Число месяца</label>
              <input
                className="tinput"
                style={{ width: 80 }}
                type="number"
                min={1}
                max={31}
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(clampInt(e.target.value, 1, 31, dayOfMonth))}
              />
            </div>
          )}

          <div className="field" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                className={`sw${workdaysOnly ? ' on' : ''}`}
                role="switch"
                aria-checked={workdaysOnly}
                tabIndex={0}
                onClick={() => setWorkdaysOnly((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setWorkdaysOnly((v) => !v);
                }}
              />
              <span style={{ fontSize: 13 }}>учитывать рабочие дни РФ (праздники/выходные)</span>
            </div>
          </div>

          <div className="field">
            <label>Часовой пояс</label>
            <select
              className="tselect"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {tzOptions.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>

          <div className="field" style={{ marginBottom: 0 }}>
            <label>Активно</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                className={`sw${active ? ' on' : ''}`}
                role="switch"
                aria-checked={active}
                tabIndex={0}
                onClick={() => setActive((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setActive((v) => !v);
                }}
              />
              <span style={{ fontSize: 13 }}>
                {active ? 'опрос приходит по расписанию' : 'опрос приостановлен (не приходит)'}
              </span>
            </div>
          </div>
        </div>

        {/* цепочка напоминаний */}
        <div>
          <div className="field" style={{ marginBottom: 8 }}>
            <label>Цепочка напоминаний (если опрос не начат/не закончен)</label>
          </div>
          <div className="tlrow">
            <span className="tltime">+0</span>
            <span className="tldot start" />
            <div className="tlbody">
              <b>Старт опроса</b>
              <small>«Опрос за сегодня 👇»</small>
            </div>
          </div>
          {steps.map((offset, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: шаги без id, порядок и есть identity
            <div className="tlrow" key={i}>
              <span className="tltime">+{OFFSET_LABELS[offset]}</span>
              <span className={`tldot${i >= 2 ? ' warn' : ''}`} />
              <div className="tlbody">
                <b>{i === steps.length - 1 ? 'Последнее напоминание' : `Напоминание ${i + 1}`}</b>
                <select
                  value={offset}
                  onChange={(e) =>
                    setSteps((cur) =>
                      cur.map((o, j) => (j === i ? (e.target.value as ReminderOffset) : o)),
                    )
                  }
                >
                  {offsets.map((o) => (
                    <option key={o} value={o}>
                      через {OFFSET_LABELS[o]}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className="iconbtn"
                title="Убрать шаг"
                onClick={() => setSteps((cur) => cur.filter((_, j) => j !== i))}
              >
                ✕
              </button>
            </div>
          ))}
          <div className="tlrow">
            <span className="tltime">→</span>
            <span className="tldot end" />
            <div className="tlbody">
              <b>Помечен «не сдан» (abandoned)</b>
              <small>попадает в дашборд как 🔴</small>
            </div>
          </div>
          {steps.length < 5 && (
            <div className="addrow" style={{ paddingLeft: 0 }}>
              <button onClick={() => setSteps((cur) => [...cur, offsets[0] ?? '1 hour'])}>
                + шаг напоминания
              </button>
            </div>
          )}
          {steps.length === 0 && (
            <p style={{ color: 'var(--muted)', fontSize: 12, margin: '8px 0 0' }}>
              Без напоминаний: опрос отправляется один раз и не дёргает повторно.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Ночной режим — справочно (read-only, движок это уже умеет)
// ============================================================

function NightModeCard() {
  return (
    <div className="card" style={{ marginTop: 4 }}>
      <h3>🌙 Ночной режим и праздники</h3>
      <p style={{ color: 'var(--muted)', fontSize: 13, margin: '0 0 8px' }}>
        Напоминания не шлются ночью (22:00–09:00 в часовом поясе сотрудника) — переносятся на
        ближайшее утро. Если включено «учитывать рабочие дни РФ», опрос пропускает выходные и
        праздники по производственному календарю. Ночное окно и набор праздников — глобальные правила
        движка, общие для всех.
      </p>
      <span className="note">
        Backend умеет произвольный cron, per-TZ старт, catchup при простое и ночное окно — веб делает
        это редактируемым без правки кода.
      </span>
    </div>
  );
}

// ============================================================
// Создание назначения (привязка анкеты к респонденту)
// ============================================================

function CreateAssignment({
  surveys,
  respondents,
  timezones,
  existing,
  onCreated,
}: {
  surveys: SurveyListResponse;
  respondents: RespondentsResponse;
  timezones: string[];
  existing: Assignment[];
  onCreated: (created: Assignment) => void;
}) {
  const activeSurveys = surveys.surveys.filter((s) => s.isActive);
  const [open, setOpen] = useState(false);
  const [surveyId, setSurveyId] = useState<string>(activeSurveys[0]?.id ?? '');
  const [respondentId, setRespondentId] = useState<string>(respondents.respondents[0]?.id ?? '');
  const [hour, setHour] = useState<number>(9);
  const [minute, setMinute] = useState<number>(0);
  const [weekdays, setWeekdays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [timezone, setTimezone] = useState<string>(timezones[0] ?? 'Asia/Yekaterinburg');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const taken = existing.some((a) => a.surveyId === surveyId && a.respondentId === respondentId);

  async function create() {
    setSaving(true);
    setError(null);
    try {
      const created = await api.createAssignment({
        surveyId,
        respondentId,
        repeat: 'weekly',
        scheduleHour: hour,
        scheduleMinute: minute,
        weekdays,
        workdaysOnly: true,
        reminderPolicy: { steps: [] },
        timezone,
      });
      onCreated(created);
      setOpen(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось создать');
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <div className="addrow" style={{ marginTop: 16 }}>
        <button onClick={() => setOpen(true)}>+ Назначить анкету сотруднику</button>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>Новое назначение</h3>
      {error && <p style={{ color: 'var(--red)', fontSize: 13, margin: '6px 0' }}>{error}</p>}
      <div className="grid2">
        <div className="field">
          <label>Сотрудник</label>
          <select
            className="tselect"
            value={respondentId}
            onChange={(e) => setRespondentId(e.target.value)}
          >
            {respondents.respondents.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} ({ROLE_LABELS[r.role] ?? r.role})
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Анкета</label>
          <select className="tselect" value={surveyId} onChange={(e) => setSurveyId(e.target.value)}>
            {activeSurveys.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title} ({s.code})
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Время (Пн–Пт по умолчанию, дальше настроите)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              className="tinput"
              style={{ width: 64 }}
              type="number"
              min={0}
              max={23}
              value={hour}
              onChange={(e) => setHour(clampInt(e.target.value, 0, 23, hour))}
            />
            <span style={{ fontSize: 18, fontWeight: 700 }}>:</span>
            <input
              className="tinput"
              style={{ width: 64 }}
              type="number"
              min={0}
              max={59}
              value={pad2(minute)}
              onChange={(e) => setMinute(clampInt(e.target.value, 0, 59, minute))}
            />
          </div>
        </div>
        <div className="field">
          <label>Дни недели</label>
          <div className="days">
            {WEEKDAYS.map((w) => (
              <button
                key={w.iso}
                className={`day${weekdays.includes(w.iso) ? ' on' : ''}`}
                onClick={() =>
                  setWeekdays((cur) =>
                    cur.includes(w.iso)
                      ? cur.filter((d) => d !== w.iso)
                      : [...cur, w.iso].sort((x, y) => x - y),
                  )
                }
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label>Часовой пояс</label>
          <select className="tselect" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            {timezones.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </div>
      </div>
      {taken && (
        <p style={{ color: 'var(--yellow)', fontSize: 13, margin: '0 0 8px' }}>
          У этого сотрудника уже есть назначение на эту анкету.
        </p>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          className="btn"
          disabled={saving || !surveyId || !respondentId || taken || weekdays.length === 0}
          onClick={create}
        >
          {saving ? 'Создаю…' : 'Создать'}
        </button>
        <button className="btn ghost" onClick={() => setOpen(false)}>
          Отмена
        </button>
      </div>
    </div>
  );
}
